import React, { useState, useEffect, useRef } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import SendIcon from '@mui/icons-material/Send';
import IconButton from '@mui/material/IconButton';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Title from './Title';

const StandaloneChatInterface = ({ assistantId, sourceToken }) => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [chatbot, setChatbot] = useState(null);
  const [stream, setStream] = useState(true);
  const [currentBotMessage, setCurrentBotMessage] = useState('');
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [thinkingVisibleMap, setThinkingVisibleMap] = useState({});
  const thinkingStartMapRef = useRef({});
  const thinkingTimesRef = useRef({});
  const [, forceUpdate] = useState(0);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const scrollToBottom = () => {
    if (!isUserScrolling && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentBotMessage]);

  useEffect(() => {
    const fetchChatbotInfo = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_API_BASE_URL + "actions/init_assistant", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ user_key: sourceToken, assistant_key: assistantId})
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          const errorMessage = errorData?.error || 'Could not fetch chatbot info';
          throw new Error(errorMessage);
        }
        
        const data = await response.json();
        setChatbot(data);
        
        if (data.messages) {
          const parsedMessages = JSON.parse(data.messages).map((msg) => {
            const key = Date.now() + Math.random();
            const isUser = msg.type === 'human';

            let text = msg.content;
            let thinking = null;

            if (!isUser) {
              const openTag = text.indexOf('<think>');
              const closeTag = text.indexOf('</think>');
              if (openTag !== -1 && closeTag !== -1 && closeTag > openTag) {
                thinking = text.slice(openTag + 7, closeTag);
                text = text.slice(0, openTag) + text.slice(closeTag + 8);
              }
              text = text.replace(/\[GENERATE_IMAGE:.*?\]/gs, '');
            }

            return {
              text,
              thinking,
              type: isUser ? 'user' : 'bot',
              key,
              isLoaded: true
            };
          });
          setMessages(parsedMessages);
        }
      } catch (error) {
        showErrorModal(error.message);
      }
    };
    fetchChatbotInfo();
  }, [assistantId, sourceToken]);

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      if ((scrollHeight - scrollTop) <= clientHeight + 10) {
        setIsUserScrolling(false);
      } else {
        setIsUserScrolling(true);
      }
    }
  };

  const toggleThinkingVisibility = (key) => {
    setThinkingVisibleMap((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const showErrorModal = (message) => {
    setErrorMessage(message);
    setErrorModalOpen(true);
  };

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (chatContainer) {
        chatContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const fetchChatbotResponse = async (message, stream = false) => {
    try {
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + 'actions/ask_assistant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_key: sourceToken, chat_key: chatbot.chat_key, assistant_key: chatbot.assistant_key, message: message })
      });
      let messageBuffer = '';

      if (stream) {
        if (!response.body) {
          throw new Error('ReadableStream not supported');
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let done = false;

        while (!done) {
          const { value, done: doneReading } = await reader.read();
          done = doneReading;
          const chunk = decoder.decode(value, { stream: true });
          messageBuffer += chunk;
          if (!chunk) continue;
          try {
            const data = JSON.parse(chunk);
            if (data.status === 'Failed') {
              const errorMsg =
                typeof data.error === 'string'
                  ? data.error
                  : data.error?.message || 'An unknown error occurred.';

              showErrorModal(errorMsg);
              break;
            }
          } catch (e) {
            setCurrentBotMessage((prevMessage) => {
              const combined = messageBuffer;

              let beforeThink = '';
              let insideThink = '';
              let afterThink = '';

              const thinkOpenTagIndex = combined.indexOf('<think>');
              const thinkCloseTagIndex = combined.indexOf('</think>');

              if (thinkOpenTagIndex !== -1 && (thinkCloseTagIndex === -1 || thinkOpenTagIndex < thinkCloseTagIndex)) {
                beforeThink = combined.slice(0, thinkOpenTagIndex);
                if (thinkCloseTagIndex !== -1) {
                  insideThink = combined.slice(thinkOpenTagIndex + 7, thinkCloseTagIndex);
                  afterThink = combined.slice(thinkCloseTagIndex + 8);
                } else {
                  insideThink = combined.slice(thinkOpenTagIndex + 7);
                }
              } else {
                if (thinkOpenTagIndex === -1) {
                  beforeThink = combined;
                } else if (thinkCloseTagIndex !== -1) {
                  beforeThink = combined.slice(0, thinkOpenTagIndex);
                  insideThink = combined.slice(thinkOpenTagIndex + 7, thinkCloseTagIndex);
                  afterThink = combined.slice(thinkCloseTagIndex + 8);
                }
              }

              let textWithoutGenerate = beforeThink + afterThink;
              textWithoutGenerate = textWithoutGenerate.replace(/\[GENERATE_IMAGE:.*?\]/gs, '');
              const generateOpenIndex = textWithoutGenerate.indexOf('[GENERATE_IMAGE:');
              if (generateOpenIndex !== -1) {
                const textFromGenerate = textWithoutGenerate.slice(generateOpenIndex);
                const generateCloseIndex = textFromGenerate.indexOf(']');

                if (generateCloseIndex === -1) {
                  textWithoutGenerate = textWithoutGenerate.slice(0, generateOpenIndex);
                }
              }
              const partialBracketMatch = textWithoutGenerate.match(/\[GENERATE_IMAGE[^\]]*$/);
              if (partialBracketMatch) {
                textWithoutGenerate = textWithoutGenerate.replace(/\[GENERATE_IMAGE[^\]]*$/, '');
              }

              const finalVisibleText = textWithoutGenerate;

              setMessages((prevMessages) => {
                const newMessages = [...prevMessages];
                const lastMsg = newMessages[newMessages.length - 1];

                let msgKey = lastMsg?.key;

                if (lastMsg?.type === 'bot') {
                  lastMsg.text = finalVisibleText;
                  lastMsg.thinking = insideThink;
                } else {
                  msgKey = Date.now();
                  newMessages.push({
                    text: finalVisibleText,
                    thinking: insideThink,
                    type: 'bot',
                    key: msgKey,
                  });
                }

                if (thinkOpenTagIndex !== -1 && msgKey && !thinkingStartMapRef.current[msgKey]) {
                  thinkingStartMapRef.current[msgKey] = Date.now();
                }

                if (thinkCloseTagIndex !== -1 && msgKey && thinkingStartMapRef.current[msgKey] && !thinkingTimesRef.current[msgKey]) {
                  const end = Date.now();
                  const duration = ((end - thinkingStartMapRef.current[msgKey]) / 1000).toFixed(1);
                  thinkingTimesRef.current[msgKey] = duration;
                  forceUpdate(n => n + 1);
                }

                return newMessages;
              });
              return combined;
            });
          }
        }
      } else {
        if (response.ok) {
          const data = await response.json();
          if (data.status === "Success") {
            return data.response;
          } else {
            throw new Error(data.error);
          }
        } else {
          throw new Error(`Request failed with status: ${response.status}`);
        }
      }
    } catch (error) {
      console.error("Error communicating with assistant:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsGenerating(true);

    let userMessage = userInput;
    setMessages((prevMessages) => [
      ...prevMessages,
      { text: userMessage, type: 'user', key: Date.now() },
    ]);

    setUserInput('');
    setCurrentBotMessage('');

    try {
      if (chatbot.chat_key !== "") {
        const botResponse = await fetchChatbotResponse(userMessage, stream);
        if (!stream) {
          setMessages((prevMessages) => [
            ...prevMessages,
            { text: botResponse, type: 'bot', key: Date.now() },
          ]);
        }
      }
    } catch (error) {
      console.error("Error fetching chatbot response:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!isGenerating && userInput.trim()) {
        handleSubmit(event);
      }
    }
  };

  return (
    <div>
      {chatbot ? (
        <>
          <Title>Chat with {chatbot.name}</Title>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '77vh' }}>
            <Paper sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }} ref={chatContainerRef}>
              {messages.map((message, index) => (
                <div key={index} style={{
                  display: 'flex',
                  padding: '5px',
                  justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start'
                }}>
                  <div
                    style={{
                      background: message.type === 'user' ? '#e0e0e0' : '#203F68',
                      color: message.type === 'user' ? '#000000' : '#FFFFFF',
                      padding: '10px',
                      borderRadius: '10px',
                      maxWidth: '60%'
                    }}
                  >
                    {message.thinking && (
                      <div
                        style={{ marginTop: '5px', fontStyle: 'italic', cursor: 'pointer' }}
                        onClick={() => toggleThinkingVisibility(message.key)}
                      >
                        {thinkingVisibleMap[message.key] ? (
                          <div style={{ color: '#ccc' }}>
                            <Markdown 
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    a: ({node, ...props}) => <a {...props} target="_blank" rel="noopener noreferrer" />
                                }}>
                              {message.thinking}
                            </Markdown>
                          </div>
                        ) : (
                          <span>
                            {message.isLoaded
                              ? 'Thought for a few seconds.'
                              : thinkingTimesRef.current[message.key]
                                ? `Thought for ${thinkingTimesRef.current[message.key]} seconds.`
                                : 'Thinking...'}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="message-markdown">
                      <Markdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                            a: ({node, ...props}) => <a {...props} target="_blank" rel="noopener noreferrer" />
                        }}>
                        {message.text}
                      </Markdown>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </Paper>
            <Box sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-end',
              paddingTop: '16px',
              borderTop: '1px solid #e0e0e0'
            }}>
              <TextField
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                multiline
                minRows={1}
                maxRows={6}
                variant="outlined"
                autoComplete="off"
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
              <IconButton
                onClick={handleSubmit}
                disabled={isGenerating || !userInput.trim()}
                color="primary"
                size="large"
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                  },
                }}
              >
                {isGenerating ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <SendIcon />
                )}
              </IconButton>
            </Box>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh'
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {errorModalOpen && (
        <Dialog
          open={errorModalOpen}
          onClose={() => setErrorModalOpen(false)}
          aria-labelledby="error-dialog-title"
        >
          <DialogTitle id="error-dialog-title">Error</DialogTitle>
          <DialogContent>
            <p>{errorMessage ?? "An unexpected error occurred."}</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setErrorModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default StandaloneChatInterface;